import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext.tsx';
import { TenantProvider } from '@/context/TenantContext.tsx';
import { AppLayout } from '@/layouts/AppLayout.tsx';
import { AdminLayout } from '@/layouts/AdminLayout.tsx';
import { DashboardHome } from '@/pages/DashboardHome.tsx';
import { LoginPage } from '@/pages/LoginPage.tsx';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage.tsx';
import { TenantsPage } from '@/pages/admin/TenantsPage.tsx';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage.tsx';
import { EmployeesPage } from '@/pages/rrhh/EmployeesPage.tsx';
import { DocumentsPage } from '@/pages/documents/DocumentsPage.tsx';
import { CategoriesPage } from '@/pages/documents/CategoriesPage.tsx';
import { MyPayrollPage } from '@/pages/MyPayrollPage.tsx';
import { EmployeeLayout } from '@/layouts/EmployeeLayout.tsx';
import { UsersPage } from '@/pages/users/UsersPage.tsx';

function NotFoundPage() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '4rem', fontWeight: 800, color: '#1e293b' }}>404</h1>
        <p style={{ color: '#64748b' }}>Página no encontrada.</p>
        <a href="/" style={{ color: '#0ea5e9', textDecoration: 'none' }}>Volver al inicio</a>
      </div>
    </div>
  );
}

// ─── Route Guards ─────────────────────────────────────────────────────────────

/**
 * Protege rutas de tenant. Si no hay sesión redirige a /login.
 * Envuelve con TenantProvider para que useParams() encuentre :tenantSlug.
 */
function RequireAuth() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
}

/**
 * Protege rutas exclusivas para rol 'employee'.
 * Admin/superadmin no tienen acceso al portal simplificado de empleado.
 */
function RequireEmployee() {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'employee') return <Navigate to="/login" replace />;
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
}

/**
 * Protege rutas de superadmin (/admin/*).
 * No necesita TenantProvider — el superadmin no tiene tenant propio.
 */
function RequireAdmin() {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'superadmin') return <Navigate to="/login" replace />;
  return <Outlet />;
}

/**
 * Redirige a la ruta de su tenant si ya tiene sesión activa.
 * Usa el tenantSlug almacenado en el token JWT (campo tenantId del AuthContext).
 */
function GuestOnly() {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated && user) {
    // user.tenantId aquí es el UUID, pero necesitamos el slug.
    // El AuthContext debe almacenar también el tenant_slug — ver nota en AuthContext.
    // Por ahora redirigimos al panel de admin si es superadmin, o a /login si no hay slug.
    if (user.role === 'superadmin') return <Navigate to="/admin" replace />;
    if (user.role === 'employee')   return <Navigate to={`/${user.tenantSlug ?? ''}/mis-nominas`} replace />;
    return <Navigate to={`/${user.tenantSlug ?? 'dashboard'}`} replace />;
  }
  return <Outlet />;
}

// ─── Router ───────────────────────────────────────────────────────────────────

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rutas públicas (solo para usuarios sin sesión) */}
        <Route element={<GuestOnly />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Rutas de tenant: /:tenantSlug/* */}
        <Route path="/:tenantSlug" element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="facturacion/*" element={<div>Facturación — módulo en construcción</div>} />
            <Route path="clientes/*" element={<div>Clientes — módulo en construcción</div>} />
            <Route path="inventario/*" element={<div>Inventario — módulo en construcción</div>} />
            <Route path="rrhh" element={<EmployeesPage />} />
            <Route path="rrhh/empleados" element={<EmployeesPage />} />
            <Route path="documentos" element={<DocumentsPage />} />
            <Route path="documentos/categorias" element={<CategoriesPage />} />
            <Route path="usuarios" element={<UsersPage />} />
            <Route path="configuracion-fiscal/*" element={<div>Configuración Fiscal — módulo en construcción</div>} />
          </Route>
        </Route>

        {/* Portal Empleado: /:tenantSlug/mis-nominas */}
        <Route path="/:tenantSlug" element={<RequireEmployee />}>
          <Route element={<EmployeeLayout />}>
            <Route path="mis-nominas" element={<MyPayrollPage />} />
          </Route>
        </Route>

        {/* Panel SuperAdmin: /admin/* — sin TenantProvider */}
        <Route path="/admin" element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="tenants" element={<TenantsPage />} />
            <Route path="planes" element={<div style={{ padding: '2rem', color: 'var(--color-muted)' }}>Planes y Facturacion SaaS — proximo modulo</div>} />
            <Route path="usuarios" element={<AdminUsersPage />} />
            <Route path="configuracion" element={<div style={{ padding: '2rem', color: 'var(--color-muted)' }}>Configuracion Global — proximo modulo</div>} />
            <Route path="logs" element={<div style={{ padding: '2rem', color: 'var(--color-muted)' }}>Logs y Monitoreo — proximo modulo</div>} />
          </Route>
        </Route>

        {/* Raíz → login */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
